import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useRef, useState, useEffect } from 'react';

import { ProductBrand } from '@common/type/GoogleTagType';
import { ProductAffiliation } from '@common/type/ProductType';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, Stack, Typography, Button, IconButton } from '@mui/material';
import Divider from '@mui/material/Divider';
import useDialogWithoutUI from 'b2c/src/hooks/useDialog';
import { useTranslation } from 'next-i18next';
import { scroller, Element } from 'react-scroll';
import cpdAPI from 'src/api/cpd';
import userAPI from 'src/api/user';
import config from 'src/config';
import { reloadAfterMergedAccount, setEnrollCookie } from 'src/helpers/auth';
import { CpdEmailValidateResult, EnrollmentStatus } from 'src/helpers/constants';
import { useCourseEnrollment, useProfile, useCpdCourseEnrollment } from 'src/helpers/use';
import { checkCpdEmail } from 'src/helpers/utils';
import {
  ApplicantTypeEnum,
  ICourse,
  CPDLicenseTypeEnum,
  ICPDCourse,
  ICustomGlance,
  PriceUnitEnum,
} from 'src/interfaces';
import { IUserIDSProfile } from 'src/interfaces/user';
import useSWR from 'swr';
import Cookies from 'universal-cookie';

import CoursesCpdDetailStyle, { CourseCpdDetailContainer, CardDescription } from './style';

import { useDialog } from '@components/atoms/dialog';
import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import CollapseCourseDescription from '@components/molecules/collapseCourseDescription';
import CurriculumCourseDetail from '@components/molecules/curriculum/curriculumCourseDetail';
import InstructorCourseDetail from '@components/molecules/instructorCourseDetail/cpd';
import CpdPreparationInformationSection from '@components/organisms/cpd/cpdPreparationInformationSection';
import Heading from '@components/organisms/cpd/heading';
import ProductCPD from '@components/organisms/cpd/productCpd';
import CpdTabScroll from '@components/organisms/cpdTabScroll';
import AuthorizeModal from '@components/share/modal/authorizeModal/AuthorizeModal';
import { sendPageViewContentCourseDetail } from '@helpers/googleTagManager';

const FooterCpdDetail = dynamic(import('@components/organisms/cpd/footerCourseDetail'));
const CitizenIdModal = dynamic(import('@components/atoms/dialog/CitizenIdModal'));
const CitizenIdDuplicateModal = dynamic(import('@components/atoms/dialog/CitizenIdDuplicateModal'));
const CitizenIdConnectOtherAccountModal = dynamic(import('@components/atoms/dialog/CitizenIdConnectOtherAccountModal'));
const CannotEnrollmentToCourseModal = dynamic(import('@components/atoms/dialog/CannotEnrollmentToCourseModal'));
const ConfirmEmailModal = dynamic(import('src/components/atoms/dialog/confirmEmail'));
const CPDEnrollment = dynamic(import('src/components/molecules/cpdEnrollment'));

type TextIcon = {
  icon?: JSX.Element;
  title?: string;
  subTitle?: string;
};

const CourseCpdDetail = () => {
  const cookies = new Cookies();

  const { t } = useTranslation('common');
  const { data: course } = useSWR<ICourse>('COURSE');
  const { profile, isLoading: isProfileLoading } = useProfile();
  const { courseData } = useCourseEnrollment(course!);

  const isSendEventGtm = useRef<boolean>(false);
  const targetRef = useRef<any>();
  const collapseSizeRef = useRef<any>();

  const router = useRouter();

  const authorizeDialog = useDialogWithoutUI({});
  const citizenIdDialog = useDialog();
  const citizenIdDuplicateDialog = useDialog();
  const citizenIdConnectOtherAccountModal = useDialog();
  const cannotEnrollToCourseDialog = useDialog();
  const emailErrorDialog = useDialog();
  const confirmEmailDialog = useDialog();

  const [canTrain, setCanTrain] = useState<boolean>(true);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [citizenId, setCitizenId] = useState<string>('');
  const [newCitizenId, setNewCitizenId] = useState<string>('');
  const [isOpenPersonalInformationForm, setIsOpenPersonalInformationForm] = useState<boolean>(false);
  const [isNewCitizenId, setIsNewCitizenId] = useState<boolean>(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [expandDescription, setExpandDescription] = useState(false);
  const [loadingEnrollment, setLoadingEnrollment] = useState(false);
  const [validatedEmail, setValidatedEmail] = useState(false);
  const [initEnrollmentStep, setInitEnrollmentStep] = useState(1);
  const [isEmailNotPass, setIsEmailNotPass] = useState(false);
  const [initEnrollmentData, setInitEnrollmentData] = useState(null);

  const salePrice = courseData.product?.salePrice || 0;
  const originalPrice =
    courseData?.product?.product_skus[0]?.product_sku_distributions.find((x) => x.price_unit === PriceUnitEnum.THB)
      ?.original_price || 0;

  const { cpdCourseEnrollment, isLoading: isCpdEnrollmentLoading } = useCpdCourseEnrollment(
    courseData?.id,
    citizenId,
    salePrice,
  );

  const getCpdBundleEnrollmentStatus = (): EnrollmentStatus => {
    if (course?.product.is_enabled === false) return EnrollmentStatus.UNAVAILABLE;
    if (!cpdCourseEnrollment && !salePrice) return EnrollmentStatus.FREE_COURSE;
    return EnrollmentStatus[cpdCourseEnrollment?.enrollStatus || EnrollmentStatus.AVAILABLE];
  };

  const scrollToComponent = (value: string) => {
    const position = () => {
      if (window.pageYOffset < targetRef?.current?.offsetHeight + 50) {
        return -150;
      }
      return -60;
    };
    scroller.scrollTo(value, {
      duration: 1000,
      smooth: true,
      offset: position(),
    });
  };

  const handleChange = (event: any, newValue: number) => {
    setSectionIndex(newValue);
    switch (newValue) {
      case 0:
        scrollToComponent('defaultGlance');
        break;
      case 1:
        scrollToComponent('coursesList');
        break;
      case 2:
        scrollToComponent('instructor');
        break;
      case 3:
        scrollToComponent('prepareTrain');
        break;
      default:
        break;
    }
  };

  const glancesVideo = () => {
    const hours = Math.floor((courseData?.default_glances?.total_hours || 0) / 3600);
    const minute = Math.floor(((courseData?.default_glances?.total_hours || 0) / 60) % 60);
    if (hours < 1) {
      return {
        icon: <FontAwesomeIcon icon="fa-light fa-circle-play" />,
        title: t('cpd_course.default_glance.all_video', { total: courseData?.default_glances?.total_videos }),
        subTitle: t('cpd_course.default_glance.all_video.time.only_minute', {
          minute,
        }),
      };
    }

    return {
      icon: <FontAwesomeIcon icon="fa-light fa-circle-play" />,
      title: t('cpd_course.default_glance.all_video', { total: courseData?.default_glances?.total_videos }),
      subTitle: t('cpd_course.default_glance.all_video.time', {
        hours,
        minute,
      }),
    };
  };

  const glancesAvailableDaysToLearn = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-hourglass-start" />,
    title: t('cpd_course.default_glance.available_days_to_learn', {
      days: (courseData?.default_glances?.available_days_to_learn || 0) + 1,
    }),
    subTitle: `${t('cpd_course.default_glance.available_days_to_learn.description')}\n${t(
      'cpd_course.default_glance.available_days_to_learn.remark',
    )}`,
  });

  const glancesCertificate = () => {
    const licenseTypes = () => {
      if (courseData?.licenseRenewal === 4) return t('cpd_course.license_types.oic.life_and_non_life');
      if (courseData?.licenseTypes?.find((f) => f === CPDLicenseTypeEnum.LIFE))
        return t('cpd_course.license_types.oic.life');
      if (courseData?.licenseTypes?.find((f) => f === CPDLicenseTypeEnum.NON_LIFE))
        return t('cpd_course.license_types.oic.non_life');
      if (courseData?.licenseTypes?.find((f) => f === CPDLicenseTypeEnum.INVESTMENT))
        return t('cpd_course.license_types.tsi.investment');
      return '';
    };
    const applicantType = () => {
      if (courseData?.applicantType === ApplicantTypeEnum.BROKER) {
        return t('cpd_course.applicant_type.broker');
      }
      if (courseData?.applicantType === ApplicantTypeEnum.AGENT) {
        return t('cpd_course.applicant_type.agent');
      }
    };

    const glanceText = () => {
      if (courseData?.licenseTypes?.find((f) => f === CPDLicenseTypeEnum.INVESTMENT))
        return t('cpd_course.default_glance.certificate.description.tsi');
      return t('cpd_course.default_glance.certificate.description.oic');
    };

    return {
      icon: <Image layout="fixed" src="/static/icons/glance-certificate.svg" width="33px" height="38px" alt="cert" />,
      title: t('cpd_course.default_glance.certificate', {
        applicant_type: applicantType(),
        license_types: licenseTypes(),
        collect_hours: courseData?.default_glances?.collect_hours,
      }),
      subTitle: glanceText(),
    };
  };

  const glancesFileDownload = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-file-arrow-down" />,
    title: t('cpd_course.default_glance.file_download'),
    subTitle: t('cpd_course.default_glance.file_download.description', {
      total: courseData?.default_glances?.total_downloaded_files,
    }),
  });

  const glancesExam = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-circle-question" />,
    title: t('cpd_course.default_glance.exam'),
    subTitle: t('cpd_course.default_glance.exam.description', { total: courseData?.default_glances?.total_quizzes }),
  });

  const glancesArticle = () => ({
    icon: <FontAwesomeIcon icon="fa-light fa-file-lines" />,
    title: t('cpd_course.default_glance.article'),
    subTitle: t('cpd_course.default_glance.article.description', {
      total: courseData?.default_glances?.total_articles,
    }),
  });

  const renderCustomGlance = () =>
    courseData.custom_glances.map(({ name = '' }: ICustomGlance, index) => (
      <Stack className="custom-glance" direction="row" key={index}>
        <FontAwesomeIcon icon="fa-light fa-check" />
        <span>{name}</span>
      </Stack>
    ));

  const renderDefaultGlance = () => {
    const list: TextIcon[] = [];
    if (courseData?.default_glances?.total_hours) list.push(glancesVideo());
    if (courseData?.default_glances?.available_days_to_learn) list.push(glancesAvailableDaysToLearn());
    if (courseData?.default_glances?.collect_hours) list.push(glancesCertificate());
    if (courseData?.default_glances?.total_downloaded_files) list.push(glancesFileDownload());
    if (courseData?.default_glances?.total_quizzes) list.push(glancesExam());
    if (courseData?.default_glances?.total_articles) list.push(glancesArticle());

    return (
      <Grid container spacing={2} sx={{}}>
        {list.map(({ icon, title = '', subTitle = '' }: TextIcon, index) => (
          <Grid item xs={12} md={6} lg={12} key={index}>
            <Box className="default-glance">
              <Box sx={{ ml: '8px', display: 'flex', textAlign: 'center', justifyContent: 'center' }}>{icon}</Box>
              <Box sx={{ marginLeft: '32px' }}>
                <Typography variant="h4" sx={{ lineHeight: '22px', whiteSpace: 'pre-wrap' }}>
                  {title}
                </Typography>
                <div className="subtitle">{subTitle}</div>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  const handleSubmitCitizenIdForm = async (value: string) => {
    const res = await userAPI.checkCitizenIdDuplicate(value);
    const userIDS: IUserIDSProfile = res.data;
    if (userIDS && profile?.email !== userIDS.email) {
      if (!userIDS.email) {
        setNewCitizenId(value);
        citizenIdDuplicateDialog.open();
      } else {
        citizenIdConnectOtherAccountModal.open();
      }
      return;
    }
    setIsNewCitizenId(true);
    setCitizenId(value);
  };

  const checkEmailValid = async (): Promise<CpdEmailValidateResult> => {
    if (!profile) return CpdEmailValidateResult.NotPass;

    const firstResult = checkCpdEmail(profile.email_validated_result_code);
    if (firstResult === CpdEmailValidateResult.Pass) {
      return CpdEmailValidateResult.Pass;
    }
    const result = await userAPI.validateEmail();
    const secondResult = checkCpdEmail(result.data.code);
    return secondResult;
  };

  const handleCheckEmail = async () => {
    setLoadingEnrollment(true);
    // check email
    if (!validatedEmail) {
      const result = await checkEmailValid();
      setValidatedEmail(true);
      if (result === CpdEmailValidateResult.Pass) {
        setIsEmailValid(true);
      } else if (result === CpdEmailValidateResult.Uncertain) {
        setIsEmailValid(false);
      } else {
        setIsEmailNotPass(true);
        emailErrorDialog.open();
        setLoadingEnrollment(false);
        return true;
      }
    }

    setLoadingEnrollment(false);
    return false;
  };

  const getGa4Items = () => {
    const coursesCategories = course?.courses_categories || [];
    return [
      {
        item_id: course?.product.product_skus[0].sku || '',
        item_name: course?.name || '',
        affiliation: course?.productAffiliation || ProductAffiliation.SKILL_LANE,
        coupon: null,
        discount: null,
        index: null,
        item_brand: ProductBrand.CPD || null,
        item_category: ProductBrand.CPD || null,
        item_category2: ProductBrand.CPD || null,
        item_category3: coursesCategories[0]?.category?.name || null,
        item_category4: coursesCategories[0]?.sub_category?.name || null,
        item_category5: null,
        item_list_id: null,
        item_list_name: null,
        item_variant: course?.contentId || null,
        location_id: null,
        price: salePrice,
        quantity: 1,
      },
    ];
  };

  const handleChooseTrainingClick = async () => {
    if (!profile) {
      authorizeDialog.onOpen();
      return;
    }
    if (profile.email.includes(profile.uid)) {
      confirmEmailDialog.open();
      return;
    }

    const isEmailError = await handleCheckEmail();

    if ((validatedEmail && isEmailNotPass) || isEmailError) {
      emailErrorDialog.open();
      return;
    }

    if (!citizenId) {
      citizenIdDialog.open();
      return;
    }

    // go to step 1
    if (canTrain && cpdCourseEnrollment && !!citizenId) {
      return setIsOpenPersonalInformationForm(true);
    }
  };

  const handleInitEnrollmentStep = async () => {
    const enrollmentCitizenId = cookies.get('cpd_enrollment_citizenId');
    const enrollmentStep = +cookies.get('cpd_enrollment_step'); // Step is 1, 2, 3, 4
    if (enrollmentStep) {
      await handleCheckEmail();
      setValidatedEmail(true);
      setInitEnrollmentStep(enrollmentStep);
      if (enrollmentStep === 2 || enrollmentStep === 3) {
        const resp = await cpdAPI.fetchTemporaryCpdEnrollmentFormData(course?.product.id!, profile?.id!);
        if (resp.data) {
          setInitEnrollmentData({
            ...resp.data,
            ...resp.data.cpdEnrollments[0],
          });
        }
      }
      if (enrollmentCitizenId) {
        setCitizenId(enrollmentCitizenId);
        setIsNewCitizenId(true);
      }
    }
  };

  useEffect(() => {
    if (profile) handleInitEnrollmentStep();
    if (!profile?.cpd_citizen_id) return;
    setCitizenId(profile.cpd_citizen_id);
  }, [profile]);

  useEffect(() => {
    if (!cpdCourseEnrollment || isCpdEnrollmentLoading) return;

    const { canEnrollToCourse, enrollStatus } = cpdCourseEnrollment;
    const isEnrolled = enrollStatus === EnrollmentStatus.ENROLLED;
    const isRegulatorUnChecked = enrollStatus === EnrollmentStatus.REGULATOR_UNCHECKED;
    const haveCitizenId = !!citizenId;

    if (!isNewCitizenId || isRegulatorUnChecked) {
      setCanTrain(canEnrollToCourse);
      return;
    }

    if (isEnrolled) return;

    if (canEnrollToCourse && haveCitizenId) {
      cookies.remove('cpd_enrollment_step', { path: '/' });
      cookies.remove('cpd_enrollment_citizenId', { path: '/' });
      setIsOpenPersonalInformationForm(true);
    }

    if (!canEnrollToCourse && haveCitizenId) {
      cannotEnrollToCourseDialog.open();
    }
  }, [cpdCourseEnrollment, isNewCitizenId]);

  useEffect(reloadAfterMergedAccount, []);

  const canEnrollToCourse = cpdCourseEnrollment?.canEnrollToCourse;
  const cpdEnrollmentStatus = getCpdBundleEnrollmentStatus();
  const isLoading = isCpdEnrollmentLoading || isProfileLoading || loadingEnrollment;

  const currentUrl = new URL(router.asPath, config.APP_DOMAIN);
  const onAuthorize = () => {
    setEnrollCookie(currentUrl.toString());
  };

  useEffect(() => {
    const isAvailable = cpdEnrollmentStatus === EnrollmentStatus.AVAILABLE;
    if (isLoading || !isAvailable || !canEnrollToCourse || isSendEventGtm.current) return;
    isSendEventGtm.current = true;
    sendPageViewContentCourseDetail({
      value: salePrice,
      currency: 'THB',
      items: getGa4Items(),
    });
  }, [isLoading, canEnrollToCourse]);

  return (
    <>
      {emailErrorDialog.render(
        <Box>
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <IconButton onClick={emailErrorDialog.close} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box display="flex" alignItems="center" flexDirection="column" rowGap="1">
            <FontAwesomeIcon style={{ fontSize: '50px' }} color="red" icon="fa-light fa-circle-exclamation" />
            <Typography variant="h4" align="center" sx={{ mt: 2 }}>
              {t('alert.email_error')}
            </Typography>
            <Button sx={{ m: 2, width: '120px' }} variant="contained" color="primary" onClick={emailErrorDialog.close}>
              {t('all_course.filter.submit')}
            </Button>
          </Box>
        </Box>,
      )}
      <AuthorizeModal
        open={authorizeDialog.isOpened}
        onClose={authorizeDialog.onClose}
        currentUrl={currentUrl.toString()}
        onAuthorize={onAuthorize}
      />
      {citizenIdDialog.render(<CitizenIdModal dialog={citizenIdDialog} onSubmit={handleSubmitCitizenIdForm} />)}
      {cannotEnrollToCourseDialog.render(
        <CannotEnrollmentToCourseModal
          dialog={cannotEnrollToCourseDialog}
          citizenId={citizenId}
          regulator={courseData.regulator}
        />,
      )}
      {citizenIdDuplicateDialog.render(
        <CitizenIdDuplicateModal
          dialog={citizenIdDuplicateDialog}
          email={profile?.email!}
          citizenId={newCitizenId}
          setCitizenId={setNewCitizenId}
        />,
      )}
      {citizenIdConnectOtherAccountModal.render(
        <CitizenIdConnectOtherAccountModal dialog={citizenIdConnectOtherAccountModal} />,
      )}
      {confirmEmailDialog.render(<ConfirmEmailModal citizenId={citizenId} dialog={confirmEmailDialog} />)}
      <Box ref={targetRef}>
        <Heading
          course={courseData!}
          onChooseTrainingClick={handleChooseTrainingClick}
          originalPrice={originalPrice}
          salePrice={salePrice}
          cpdEnrollmentStatus={cpdEnrollmentStatus}
          isLoading={isLoading}
        />
      </Box>
      <CoursesCpdDetailStyle>
        <div className="tab-nav" style={{ backgroundColor: '#F5F5F5' }}>
          <CpdTabScroll headingHeight={targetRef?.current?.offsetHeight} value={sectionIndex} onChange={handleChange} />
        </div>
        <Box className="container" sx={{ marginTop: '30px' }}>
          <div>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12} lg={8} order={{ lg: 1, xs: 2 }}>
                <Element name="coursesDetail" />
                <div>
                  <div className="course-description-title-desktop">
                    <Typography color="black" variant="h4" fontWeight="500" className="course-description-title">
                      {t('cpd_course.course_description.title')}
                    </Typography>
                    <Box mt={1} mb={2}>
                      <Divider />
                    </Box>
                  </div>
                  {courseData.custom_glances.length > 0 && (
                    <CardDescription>
                      <Typography color="primary.main" variant="h6_deprecated">
                        {t('cpd_course.course_description.result')}
                      </Typography>
                      {renderCustomGlance()}
                    </CardDescription>
                  )}

                  <Box paddingY="10px">
                    <CollapseCourseDescription
                      seoDescription={course?.seoDescription}
                      description={course?.description!}
                    />
                  </Box>
                </div>
              </Grid>
              <Grid item xs={12} md={12} lg={4} order={{ lg: 2, xs: 1 }}>
                <Element name="defaultGlance" />
                <div className="course-description-title-mobile">
                  <Typography color="black" variant="h4" fontWeight="500">
                    {t('cpd_course.course_description.title')}
                  </Typography>
                  <Box mt={1} mb={4}>
                    <Divider />
                  </Box>
                </div>
                <div>{renderDefaultGlance()}</div>
              </Grid>
              <Grid item xs={12} md={12} lg={8} order={{ lg: 3, xs: 3 }}>
                <CurriculumCourseDetail parts={courseData.parts!.filter((part) => part.course_items.length)} />
              </Grid>
              <Grid item xs={12} md={12} lg={4} order={{ lg: 4, xs: 4 }}>
                <Element name="instructor" />
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="flex-start"
                  sx={{ marginBottom: 1, height: '40px' }}
                >
                  <Typography color="black" variant="h4" fontWeight="500">
                    {t('cpd_course.tab_menu.instructor')}
                  </Typography>
                </Grid>
                <InstructorCourseDetail course={courseData} />
              </Grid>
            </Grid>
          </div>
        </Box>
      </CoursesCpdDetailStyle>
      <CpdPreparationInformationSection />
      <CourseCpdDetailContainer>
        <Box my={{ xs: '24px', lg: '48px' }}>
          <ProductCPD />
        </Box>
      </CourseCpdDetailContainer>
      <FooterCpdDetail
        course={courseData!}
        height={targetRef?.current?.offsetHeight}
        onChooseTrainingClick={handleChooseTrainingClick}
        originalPrice={originalPrice}
        salePrice={salePrice}
        canEnrollToCourse={canEnrollToCourse}
        cpdEnrollmentStatus={cpdEnrollmentStatus}
        isLoading={isLoading}
      />
      <CPDEnrollment
        step={initEnrollmentStep}
        open={isOpenPersonalInformationForm}
        onClose={() => setIsOpenPersonalInformationForm(false)}
        cpdCourseEnrollment={cpdCourseEnrollment!}
        isEmailValid={isEmailValid}
        cpdCourse={course! as ICPDCourse}
        initEnrollmentData={initEnrollmentData}
      />
    </>
  );
};

export default CourseCpdDetail;
